import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { usePlansQuery } from "../hooks/usePlans";
import type { Plan } from "../types";

const PlansPage = () => {
  const { data, isLoading, isFetching } = usePlansQuery();
  const plans = data?.data ?? [];
  const isBusy = isLoading || isFetching;

  const linkedCount = plans.filter((p) => p.creemProductId).length;
  const dedicatedPlans = plans.filter((p) => p.type === "dedicated");
  const litePlans = plans.filter((p) => p.type === "lite");
  const unlinkedPlans = plans.filter((p) => !p.creemProductId);

  const renderTable = (title: string, items: Plan[]) => (
    <Card className="border-neutral-800 bg-neutral-900/50">
      <div className="border-b border-neutral-800 px-4 py-3">
        <h3 className="text-sm font-medium text-neutral-200">{title}</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-neutral-800 hover:bg-transparent">
            <TableHead className="text-neutral-400">Plan</TableHead>
            <TableHead className="text-neutral-400">Precio</TableHead>
            <TableHead className="text-neutral-400">Specs</TableHead>
            <TableHead className="text-neutral-400">Horas/Semana</TableHead>
            <TableHead className="text-neutral-400">Creem Product ID</TableHead>
            <TableHead className="text-neutral-400">Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((plan) => (
            <TableRow
              key={plan.id}
              className="border-neutral-800 hover:bg-neutral-800/50"
            >
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium text-neutral-100">
                    {plan.name}
                  </span>
                  {plan.key && (
                    <span className="text-xs text-neutral-500">{plan.key}</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <span className="text-sm font-medium text-neutral-100">
                  ${plan.monthlyPrice}/
                  {plan.currency === "usd" ? "mes" : plan.currency}
                </span>
              </TableCell>
              <TableCell>
                {plan.cores != null && plan.ram != null ? (
                  <span className="text-sm text-neutral-300">
                    {plan.cores} vCPU / {plan.ram} GB RAM
                  </span>
                ) : (
                  <span className="text-xs text-neutral-500">—</span>
                )}
              </TableCell>
              <TableCell>
                {plan.weeklyStreamingHours ? (
                  <span className="text-sm text-amber-400">
                    {plan.weeklyStreamingHours} hs
                  </span>
                ) : (
                  <span className="text-sm text-green-400">24/7</span>
                )}
              </TableCell>
              <TableCell>
                {plan.creemProductId ? (
                  <span
                    className="text-xs text-neutral-400 font-mono truncate max-w-[200px] block"
                    title={plan.creemProductId}
                  >
                    {plan.creemProductId}
                  </span>
                ) : (
                  <span className="text-xs text-neutral-500">—</span>
                )}
              </TableCell>
              <TableCell>
                {plan.creemProductId ? (
                  <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
                    Vinculado
                  </Badge>
                ) : (
                  <Badge className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30">
                    Sin vincular
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Planes" subtitle="Planes vinculados con Creem" />

      {isBusy && <LoadingOverlay />}

      {!isLoading && plans.length === 0 && (
        <EmptyState
          title="Sin planes"
          description="No hay planes configurados. Crea planes locales y vincula su Creem Product ID."
        />
      )}

      {dedicatedPlans.length > 0 &&
        renderTable("Planes Dedicados (24/7)", dedicatedPlans)}
      {litePlans.length > 0 &&
        renderTable("Planes Lite (On-demand)", litePlans)}

      {plans.length > 0 && (
        <div className="text-xs text-neutral-400">
          Total: {plans.length} plan{plans.length !== 1 ? "es" : ""} ·{" "}
          {linkedCount} vinculado
          {linkedCount !== 1 ? "s" : ""} · {unlinkedPlans.length} sin vincular
        </div>
      )}
    </div>
  );
};

export default PlansPage;
